import { FormikHelpers } from 'formik'
import React from 'react'
import { Props as BadgeProps } from '../components/ProductBadge/ProductBadge'
import { BackgroundColor, SpotlightBannerBackgroundColor } from '../styles/jss/utils'
import { Props as SalesBadgeProps } from '../components/SalesBadge/SalesBadge'
import { SecondaryBadgeProps } from '../components/SecondaryBadge/SecondaryBadge'
import { colors } from '../styles/jss/variables'
import { Breakpoint, SelectChangeEvent } from '@mui/material'
export enum ROUNDELS {
  ROUNDEL_1 = 'roundel1',
  // We have unused these roundels
  // ROUNDEL_2 = 'roundel2',
  // ROUNDEL_3 = 'roundel3',
  // ROUNDEL_4 = 'roundel4',
  // ROUNDEL_5 = 'roundel5',
}

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'secondaryDark'
  | 'secondaryLight'
  | 'tertiary'
  | 'tertiaryDark'
  | 'tertiaryLight'

export type LinkProps = {
  label: string
  url?: string
  icon?: string
  iconPosition?: 'left' | 'right' | 'top'
  iconFontSize?: 'inherit' | 'small' | 'large'
  highlight?: boolean
  className?: string
  nofollow?: boolean
  noFollow?: boolean
  newWindow?: boolean
  onClick?: (e?: any) => void
  // Added to support links that appear as buttons
  button?: boolean
  variant?: ButtonVariant
  thin?: boolean
  tabIndex?: number
  highlightTextMatch?: string
  id?: string
  roundel?: ROUNDELS
  rel?: boolean
  triggerLinkAnalytics?: (linkText: string) => void
}

export type LinkGroup = {
  titleLink: LinkProps
  links: LinkProps[]
}

export type LinkItem = {
  link: LinkProps
  linkGroups: LinkGroup[]
  products?: ProductData[]
  spotlightProducts?: SpotlightProductCardProps[]
}

export type MenuItems = LinkItem[]

export type MyAccountMenuItems = {
  icon: string
  title: string
  description: string
  url: string
}

export type ImageBreakpoint = {
  breakpoint?: Breakpoint
  query?: 'min-width' | 'max-width'
  filename: string
  sizes?: string
}

export type ImageSize = {
  breakpoint?: Breakpoint
  query?: 'min-width' | 'max-width'
  width: string
}

export type Image = {
  url: string
  altText: string
  label?: string
  badge?: BadgeProps
  ffSalesBadge?: SalesBadgeProps
  secondaryBadge?: SecondaryBadgeProps
}
export type MobileImage = {
  url: string
  altText: string
}

export type Video = {
  url: string
  loop?: boolean
  autoPlay?: boolean
  controls?: boolean
  preload?: 'auto' | 'metadata' | 'none'
  resize?: boolean
  snippet?: boolean
  width?: string
  height?: string
  muted?: boolean
  poster?: string
  playButton?: boolean
  playSound?: boolean
  thumbnailURL?: string
}
export type IdpCardProps = {
  membershipProductSKU: string
  image?: Image
  isEnableProductAsRecommended?: boolean
}

export type Images = Image[]

type DaysOfWeek = 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday'

export interface Attribute<Name = string, Value = any> {
  name: Name
  value: Value
}

export type Category = {
  id: string
  key: string
  name: string
  url: string
  ancestors?: Category[]
}

type Attributes<Name = string, Value = any> = {
  attributes: Attribute<Name, Value>[]
}

interface ProductAttribute
  extends Attribute<
    | 'sameDay'
    | 'daysAvailable'
    | 'deliveryTypes'
    | 'crossSellProducts'
    | 'includeInSiteMap'
    | 'promotionalText'
    | 'giftDetails'
    | 'usps'
    | 'containsAlcohol'
    | 'productTypeIdentifiers'
    | 'isFloristChoice'
    | 'arrangementType'
    | 'keyProductMessaging',
    string | boolean | Object | null
  > {}

interface ProductVariantAttribute
  extends Attribute<
    | 'variantSize'
    | 'variantColor'
    | 'occasions'
    | 'flowerName'
    | 'flowerColor'
    | 'contains'
    | 'mayContain'
    | 'suitableFor'
    | 'allergenURL'
    | 'giftContents'
    | 'strikeThroughPrice'
    | 'wasPrice',
    'openPrice' | string | string[] | null
  > {}

type AlgoliaProduct = {
  key: string
  sku: string
  productName: string
  variantName: string
  description: string
  promotionalText: string
  allCategories: string[]
  arrangementType: string
  containsAlchohol: boolean
  deliveryTypes: string[]
  flowerName: string[]
  occasions: string[]
  price: number
  productTypeIdentifiers: string[]
  rotation: string
  sameDay: boolean
  specialIndicator: string[]
  flowerColor: string[]
  data: ProductData
  objectID?: string
  __position?: string
  __queryID?: string
}

type AlgoliaQueryType = 'PDP' | 'PLP' | 'Search' | 'Autocomplete' | 'GuidedNav'

export type ProductData = {
  id?: string
  name: string
  description: string
  slug: string
  url: string
  price: ProductPrice
  variantColor: string[]
  variantSize: string[]
  images: Images
  productTypeIdentifiers?: string[]
  promotionalText?: string
  sameDay?: boolean
  uniqueServicePropositions?: string[]
  sku?: string
  key?: string
  categoryNames?: string[]
  categoryUsps: string[]
  wasPrice?: string
  queryId?: string
  queryType?: AlgoliaQueryType
  objectId?: string
  position?: string
  indexName?: string
  isProductOutOfStock?: boolean
  hasProductBadge?: boolean
  heightOfBadge?: number
  deliveryPassPromotionText?: string
  typeKey?: string
  firstDeliveryMessage?: string
  lastDeliveryMessage?: string
  keyProductMessaging?: string[]
}

type ListProduct = {
  id?: string
  sku?: string
  key?: string
  images: Images
  name: string
  url: string
  price: ProductPrice
  uniqueServicePropositions?: string[]
  variants?: ListProductVariant[]
  description?: string
  attributes?: ProductAttribute[]
  // Algolia returns attributes directly on product
  sameDay?: boolean
  promotionalText?: string
  productTypeIdentifiers?: string[]
  categoryUsps: string[]
}

export type DetailProduct = {
  id?: string
  name: string
  url: string
  variants: DetailProductVariant[]
  price: ProductPrice
  images: Images
  key?: string
  sku?: string
  slug?: string
  description?: string
  metaDescription?: string
  isFuneral?: boolean
  attributes?: ProductAttribute[]
  countryCode?: string
  categoryNames?: string[] | null
  isInternational?: boolean
  isOverseas?: boolean
  wasPrice?: string
  isCourierUpgrade?: boolean
  isProductOutOfStock?: boolean
  isfloristChoice?: boolean
  isDropship?: boolean
  promotionalText?: string
  typeKey?: string
}

export type FinishingTouchProduct = Omit<ListProduct, 'sku' | 'attributes' | 'variants'> & {
  sku: string
  description: string
  variants?: FinishingTouchProductVariant[]
}

interface DigitalProductWithKeyMessaging extends Attributes<'keyProductMessaging'> {}

export type DigitalProductForComparison = ProductData & {
  variants?: DigitalProductWithKeyMessaging[]
}

export type FinishingTouchProductGroup = {
  categoryName: string
  products: FinishingTouchProduct[]
}

interface ListProductVariant extends Attributes<'variantColor' | 'variantSize'> {}

interface FinishingTouchProductVariant
  extends Attributes<'occasions' | 'contains' | 'mayContain' | 'suitableFor' | 'allergenURL'> {
  isMaster: boolean
}

export interface DetailProductVariant extends Attributes {
  key?: string
  sku: string
  isMaster: boolean
  name?: string
  price: ProductPrice
  images: Images
}

export type ProductPrice = {
  currencyCode: string
  centAmount: number
  fractionDigits: number
  formatted: string
}

export type DeliverySettings = {
  sameDayCutoff: string
  deliveryHours: string
  deliveryCharge: number
  collectionHours: string
}

export type FieldValidation = {
  state?: 'error' | 'success'
  errorMessage?: string
}

type ValidationObject = {
  [key: string]: {
    type: 'number' | 'string' | 'date'
    rules: {
      rule: 'min' | 'max' | 'required' | 'email'
      params: number[] | string[]
    }[]
  }
}

export type DateSelection = Date | [Date, Date] | null | undefined

export type Address = {
  id?: string
  firstName?: string
  lastName?: string
  addressLine1?: string
  addressLine2?: string
  addressLine3?: string
  city?: string
  county?: string
  postalCode?: string
  country?: string
  phone?: string
  mobile?: string
  email?: string
  isDefaultAddress?: boolean
  deliveryInstructionNote?: string
  deliveryInstructionOption?: string
}

export type LineItem = {
  id: string
  sku: string
  productId?: string
  name: string
  url: string
  price: ProductPrice
  quantity: number
  images: Images
  occasion?: string
  cardMessage?: string
  deliveryDate?: string
  deceasedName?: string
  funeralServiceDate?: Date | string
  funeralServiceTime?: string
  promotionalText?: string
  deliveryInstructionOption: string
  deliveryInstructionNote?: string
  specialInstructions?: string
  category?: { name: string; ancestors?: { name: string }[] }
  product?: {
    key: string
    name: string
    attributes?: ProductAttribute[]
    isInternational?: boolean
    categoryNames?: string[]
    isOverseas?: boolean
    isDropship?: boolean
    countryCode?: string
    objectId?: string
  }
  attributes?: ProductVariantAttribute[]
  isGiftItem?: boolean
  openPrice?: string
  stemPricing?: boolean
  stemMinimum?: number
  stemIncrementalPrice?: number
  stemMaximum?: number
  stemType?: string
  stemSameDayMax?: number
}

export type Recipient = {
  firstName: string
  lastName: string
  phone: string
}

export type LineItemGroup = {
  lineItem: LineItem
  finishingTouchLineItems?: LineItem[]
  recipient?: Recipient
}

export type Delivery = {
  date?: string
  address: Address
  serviceCode: string
  serviceDescription: string
  serviceType: string
  charge: ProductPrice
}

export type Collection = {
  date: string
  address: CollectionShopAddress
}

export type CollectionShopAddress = {
  rotationWeight?: string
  executingMemberNo?: string
  latitude?: string
  longitude?: string
  distance?: string
  openingTime?: string
  closingTime?: string
  countyName?: string
  postCode?: string
  countryType?: string
  name?: string
  addLine2?: string
  town?: string
  phoneNo?: string
  custLatitude?: string
  custLongitude?: string
}

export type Consignment = {
  id: string
  orderNumber?: string
  orderStatus?: OrderStatus
  rotation: string
  delivery?: Delivery
  items: LineItemGroup[]
  isDigital?: boolean
  isMaxQuantity: boolean
  orderTracking?: OrderTracking | null
  collection?: Collection
  isClickAndCollect?: boolean
}
type OrderTracking = {
  image?: string
  trackingUrl?: string | null
}
export type BaseOrder = {
  // Adding id will cause apolloClient caching issues if result doesn't have globally unique address id
  // Issue with historic orders query where order is being split into two, id only used on confirmation page
  id?: string
  billingAddress: Address
  billingAddressCountrySelector?: string
  showCountrySelector?: boolean
  billingAddressSelection?: string
  useDeliveryAddress: string
  totalPrice: ProductPrice
  deliveryCost: ProductPrice
  deliveryPassType: string | null
  deliveryPassDiscount: ProductPrice | null
  totalDeliveryPassDiscount: ProductPrice | null
  couponCode?: string | null
  couponDiscount?: ProductPrice | null
  marketing: { online: boolean; post: boolean; email: boolean; sms: boolean }
  totalItems?: number
  subtotal: ProductPrice
  consignments: Consignment[]
  isLegacy?: boolean
  orderTracking?: FarEyeOrderTracking
  isFarEye?: boolean
  orderStatusMessage?: string
  orderStatusStep?: number
  orderStatusIconColor?: string
}

type FarEyeOrderTracking = {
  statusEvent: string
  statusDateTime: string
  deliveryReason: string
  deliveryNote: string
  failureReason: string
  failureNote: string
  cancellationReason: string
  collectionReason: string
  attemptCount: string
  trackingUrl: string
  signature: string
  image: string
}

export type UnavailableItem = {
  lineItemId: string
  name: string
  checkoutError?: string[]
}
export type Cart = BaseOrder & {
  cutOff?: { secondsRemaining?: number; day?: string } | null
  maxCheckoutStep: string
  worldpayRedirectUrl?: string
  paymentMethod?: string
  unavailableItems?: UnavailableItem[]
  showMarketingPreferences: boolean
}

export type Order = BaseOrder & {
  orderNumber?: string
  orderStatus?: OrderStatus
  createdAt: string
  customerCanRegister?: boolean
  isClickAndCollect?: boolean
}

type OrderStatus = 'OR' | 'OAM' | 'OOD' | 'DEL' | 'DA' | 'DC' | 'UKN'

type GenericGalleryProps = {
  heading?: string
  body?: string // markup
  messageBefore?: string
  linksBefore?: LinkProps[]
  items?: any[] // Product / Block
  messageAfter?: string
  linksAfter?: LinkProps[]
  linkStyle?: 'TEXT' | 'BUTTON'
}

export type Filter = {
  attributeName: string
  attributeLabel: string
  isOpen: boolean
}

export type GenericContentProps = {
  image?: Image
  mobileImage?: MobileImage
  mobileImageBefore?: MobileImage
  mobileImageAfter?: MobileImage
  imageBefore?: Image
  imageAfter?: Image
  heading?: string | React.ReactNode
  subheading?: string
  message?: string | React.ReactNode
  citation?: string
  link?: LinkProps
  links?: LinkProps[]
  linkStyle?: 'TEXT' | 'BUTTON'
  backgroundColor?: BackgroundColor
  headingColor?: 'JADE' | 'COBALT' | 'WILD_EXOTIC' | keyof (typeof colors)['genericContentHeading']
  headingTag?: 'DIV' | 'H1' | 'H2' | 'H3' | 'H4'
  textAlignment?: 'LEFT' | 'RIGHT' | 'CENTER'
  contentPosition?: 'LEFT' | 'RIGHT' | 'CENTER'
  mobileContentPosition?: 'ABOVE' | 'BELOW' | 'OVER'
  isQuotation?: boolean
  font?: 'SOHNE'
  darknessLevel?: number
  minHeight?: 'SMALL' | 'MEDIUM' | 'LARGE' | 'BLOG'
  buttonVariant?: ButtonVariant
  video?: Video
  showTrustpilotWidget?: boolean
  trustpilotBackground?: 'WHITE' | 'BLACK'
  trustPilotDarknessLevel?: number
  isDeliveryPassInnerContentClass?: boolean
  videoSchema?: string
  textColor?: 'WHITE' | 'BLACK'
  textContentPosition?: 'TOP' | 'CENTER' | 'BOTTOM'
}

export type Nullable<T> = { [P in keyof T]: T[P] | null }

export type SortConfig = {
  value: string
  label: string
}[]

export type LooseObject = {
  [key: string]: any
}

export type HandleSubmit<Values> = (values: Values, formikHelpers?: FormikHelpers<Values>) => Promise<any>

type HandleRedirectSubmit<Values> = (
  values: Values,
  formikHelpers?: FormikHelpers<Values>,
  displayErrors?: (errors: LooseObject) => void
) => Promise<void>

export type RegisterValues = {
  firstName: string
  lastName: string
  email: string
  password: string
  marketing: boolean
  optInEmail: boolean
  optInOnline: boolean
  optInSms: boolean
  optInPost: boolean
}

export type SignInValues = {
  email: string
  password: string
}

export type FullNameValues = {
  firstName: string
  lastName: string
}

export type PhoneNumberValue = {
  contactNumber: string
}

export type updatePasswordValues = {
  currentPassword: string
  newPassword: string
}
export type preferenceValues = {
  optInEmail: boolean
  optInPost: boolean
  optInOnline: boolean
  optInSms: boolean
}

export type PrivacyPreferenceValues = {
  email: boolean
  sms: boolean
  post: boolean
  digitalMarketing: boolean
}

export type EmailAddressValue = {
  email: string
}

export type PasswordValue = {
  password: string
}

export type GuestCheckoutValue = {
  guestCheckout: boolean
}

export type FormattedAddress = (vars: { suggestion: string; format: string }) => Promise<any>

export type FormattedAddressAndProductAvailability = (values: {
  suggestion: string
  format: string
  variantKey?: string
  address?: Address
  deliveryType?: string
  latitude?: string
  longitude?: string
}) => Promise<any>

export type ProductAvailability = (values: {
  startDate: string
  endDate: string
  variant: string
  occasion?: string
  address: ProductAvailabilityMemberAddressInput
}) => Promise<any>

type ProductAvailabilityMemberAddressInput = {
  addressLine1?: string
  addressLine2?: string
  addressLine3?: string
  city: string
  county: string
  postalCode: string
  country: string
}

export type DeliveryOptions = (vars: {
  variant?: string
  deliveryDate: string
  ruleIds?: string[]
  deliveryPassType?: string
  countryCode?: string
  address?: Address
}) => Promise<any>

export type HandleDeliveryOptionsCall = (
  deliveryDate: string,
  ruleIds: string[],
  formikContext: any,
  queryOnly?: boolean,
  deliveryPassType?: string,
  address?: Address,
  isFromDateChange?: boolean,
  selectedServiceDescription?: string
) => Promise<any>

export type IsProductFulfillable = (vars: {
  deliveryDate: string
  deliveryServiceCode: string
  variant: string
  address: ProductAvailabilityMemberAddressInput
  funeralServiceTime?: string
  funeralServiceDate?: string
}) => Promise<any>

export type AvailabilityDay = {
  date: string
  deliveryServiceTypes: {
    serviceCode: string
    ruleId: string
  }[]
}

export type DeliveryOption = {
  title: string
  price: {
    centAmount: number
    currencyCode: string
    fractionDigits: number
    formatted: string
  }
  serviceCode?: string
  serviceType?: string
  standardRuleId?: string
  standardDeliveryCharge?: string
  optionalRuleId?: string | null
  optionalDeliveryCharge?: string | null
  isIncludedInDeliveryPass: boolean
  isCourierOption?: boolean
  deliveryPassPromotionText?: string
  deliveryPassPromotionType?: string
}

type CustomerOccasionReminder = {
  id: string
  firstName: string
  lastName: string
  occasion: string
  date: string
}

export type Customer = {
  id: string
  firstName: string
  lastName: string
  customerNumber?: string
  contactNumber?: string
  email: string
  defaultShippingAddress?: Address
  defaultBillingAddress?: Address
  shippingAddresses?: Address[]
  billingAddresses?: Address[]
  deliveryPass: {
    expiryDate?: string
    isRenewable?: boolean
    product?: ProductData
    availableToPurchase?: ProductData[]
    type?: string
  }
}

export type SvgImage = {
  url: string
  altText: string
}

export type ConfirmationRegisterValues = {
  password: string
  orderId: string
  occasionReminders?: ReminderProps[]
}

export type postcodeValue = {
  postcode: string
}

type BlogArticleCategoryValues = {
  url: string
  name: string
}

export type AlgoliaBlogLatestArticles = {
  title: string
  publishedDate: string
  message: string
  bannerImage: Image
  blogCategories: BlogArticleCategoryValues[]
  data: BlogLatestArticle
}

export type BlogLatestArticle = {
  title: string
  image: Image
  categories: BlogArticleCategoryValues[]
  date: string
  description?: string
  link?: LinkProps
  backgroundColor?: 'FF_SHARP_YELLOW' | 'FF_PLUSH_BLUSH' | 'FF_LAZY_LIME' | 'FF_WHITE'
  authorInformation?: {
    name?: string
    image?: Image
    backgroundColor?: BackgroundColor
  }
}

export type PromotionalCodeValue = {
  promoCodeField: string
}

export type NewsletterSignUpValues = {
  firstName: string
  lastName: string
  email: string
}

export enum DigitalProductTypes {
  GOLD = 'GOLD',
  PLATINUM = 'PLATINUM',
}

export enum DigitalProductNames {
  INTERFLORA_GOLD = 'Interflora Gold',
  INTEFRFLORA_PLATINUM = 'Interflora Platinum',
}

export type addressTown = {
  locationName: string
  postalCode: string
}
export enum CheckoutErrors {
  PRODUCT_UNAVAILABLE = 'PRODUCT_UNAVAILABLE',
  MEMBER_UNAVAILABLE = 'MEMBER_UNAVAILABLE',
  DELIVERY_DATE_PASSED = 'DELIVERY_DATE_PASSED',
}

export type CountryList = { name: string; codeAlpha2: string; codeAlpha3: string }[]

export type CartCount = {
  totalConsignments?: number
}

export type ConfirmationReminderOccasionValues = {
  occasionReminders: ReminderProps[]
}

export type OccasionReminder = {
  isActive?: boolean
  isPredeterminedDate?: boolean
  preDeterminedOccasionDate?: string
  occasionIcon: string
  categoryLinkTitle: string
  occasionCategoryUrl: string
}

export type ReminderProps = {
  occasion: string
  firstName: string
  lastName: string
  icon?: string
  date: string
  id?: string
  isAlreadyExist?: boolean
}

export type AddEditOccasionValues = {
  firstName: string
  lastName: string
  occasion: string
  day?: string
  month?: string
  id?: string
}

export type ReminderStatus = {
  reminderStatus?: 'alertInfo' | 'alertError' | 'alertSuccess'
  reminderStatusMessage?: string
}

export type CountriesListProps = {
  url: string
  label: string
}[]

export type ConfirmationSignInValues = {
  email: string
  password: string
  orderId?: string
  occasionReminders?: ReminderProps[]
}

export type DeliveryOptionsInfo = {
  title?: string
  content?: string
}
export type DeliveryOptionsOrAlternateProducts = (
  productKey: string,
  variantKey: string,
  deliveryDate: string,
  ruleIds?: string[],
  deliveryPassType?: string,
  countryCode?: string,
  address?: Address,
  occasion?: string
) => Promise<any>

export type CCProductAvailability = (
  productKey: string,
  variantKey: string,
  deliveryDate: string,
  countryCode?: string,
  address?: Address,
  latitude?: string,
  longitude?: string
) => Promise<any>

export type HandleDeliveryOptionsOrAlternateProducts = (
  deliveryDate: string,
  ruleIds?: string[],
  deliveryPassType?: string,
  countryCode?: string,
  address?: Address,
  occasion?: string
) => Promise<any>

export type HandleCCProductAvailability = (
  deliveryDate: string,
  countryCode?: string,
  address?: Address
) => Promise<any>

export type DeviceTypes = 'MOBILE' | 'TABLET' | 'DESKTOP' | 'LARGEDESKTOP' | 'SMALL'

export enum PaymentMethodTypes {
  CARD = 'card',
  PAYPAL = 'payPal',
  KLARNA = 'klarna',
  KLARNA_PAY_SLICED = 'klarnaPaySliced',
  KLARNA_PAY_LATER = 'klarnaPayLater',
  KLARNA_PAY_NOW = 'klarnaPayNow',
}

export type SpotlightProductCardProps = {
  image: Image
  promoText?: string
  backgroundColor?: SpotlightBannerBackgroundColor
  links?: LinkProps[] | undefined
  className?: string
  columnWidth?: '1' | '2'
  cardLink?: string
}

export type PaymentTypeProps = {
  type:
    | PaymentMethodTypes.CARD
    | PaymentMethodTypes.PAYPAL
    | PaymentMethodTypes.KLARNA
    | PaymentMethodTypes.KLARNA_PAY_SLICED
    | PaymentMethodTypes.KLARNA_PAY_LATER
    | PaymentMethodTypes.KLARNA_PAY_NOW
    | PaymentMethodTypes.KLARNA
  icons: string[]
  description: string
  buttonLabel: string
}

type URLProps = {
  label?: string
  url?: string
}

export type InspirationCardProps = {
  title?: string
  image?: Image
  date?: string
  link?: URLProps
  queryId?: string
  queryType?: AlgoliaQueryType
  objectId?: string
  position?: string
  indexName?: string
}

export enum SearchOptionsList {
  CATEGORIES = 'Categories',
  INSPIRATIONS = 'Inspiration',
  PRODUCTS = 'Products',
  GIFTS = 'Gifts',
}

export const KLARNA_PAY_SLICED = 'klarnaPaySliced'
export const KLARNA_PAY_LATER = 'klarnaPayLater'
export const KLARNA_PAY_NOW = 'klarnaPayNow'

export const klarnaPaymentOptions: string[] = [
  PaymentMethodTypes.KLARNA_PAY_SLICED,
  PaymentMethodTypes.KLARNA_PAY_LATER,
  PaymentMethodTypes.KLARNA_PAY_NOW,
]

export const klarnaTypes: string[] = [PaymentMethodTypes.KLARNA, ...klarnaPaymentOptions]

export type NBHits = {
  [key: string]: number | string
}

export type StyleVariantInfoProps = {
  meta?: any
  title: string
  body: string
  image?: Image
}

export type GuidedNavProps = {
  title?: string
  basicInitiator?: boolean
  isOpenControlled?: boolean
  setOpenControlled?: (value: boolean | ((prevVar: boolean) => boolean)) => void
  headingTag?: 'DIV' | 'H1' | 'H2' | 'H3' | 'H4'
  countriesList?: CountriesListProps
  clickPostCodeChecker?: () => void
  trackPostCodeCheckerDate?: () => void
  trackPostCodeCheckerAddress?: (pcc_postcode: string, pcc_city: string, pcc_county: string) => void
  trackPostCodeCheckerManualAddress?: () => void
  trackPostCodeCheckerOccasion?: (occasion: string) => void
  submitPostCodeCheckerComplete?: () => void
  trackPostCodeCheckerManualAddressClick?: () => void
  trackEnterManualAddressClick?: (click_location: string) => void
  backgroundColor?: 'GREY' | 'RED'
  availableSundays?: string[]
  bankHolidays?: string[]
  locationInternationalDelivery?: (click_location: string) => void
  setShowInternationalLink?: (showInternationalLink: boolean) => void
  showInternationalLink?: boolean
  trackPCCCloseAnalytics?: (location: string) => void
  trackPCCStartNewSearch?: () => void
}

export type AuthAnalyticProps = {
  journeyType: 'LOGIN' | 'REGISTER' | 'GUEST'
  isSuccessful?: boolean
  email?: string
}

export type WeekDays = {
  monday: boolean
  tuesday: boolean
  wednesday: boolean
  thursday: boolean
  friday: boolean
  saturday: boolean
  sunday: boolean
}

export type TimedContentProps = {
  startTime: string
  endTime: string
  icon?: string
  weekDays: WeekDays
  messages: string[]
}

export type SaveUserJourneyAnalyticsSubmit<Values> = (values: Values) => void

export type MediaCarousalProps = {
  type?: string
  image?: Image
  shouldZoom?: boolean
  heading?: string
  description?: string
  video?: Video
}

export type CSSFromLaunchDarklyFlag = { xs?: string; sm?: string; md?: string; lg?: string; xl?: string }

export enum AllDays {
  SUNDAY = 'Sunday',
  MONDAY = 'Monday',
  TUESDAY = 'Tuesday',
  WEDNESDAY = 'Wednesday',
  THURSDAY = 'Thursday',
  FRIDAY = 'Friday',
  SATURDAY = 'Saturday',
  MONDAY_TO_FRIDAY = 'Monday to Friday',
}

export enum OpenFlags {
  CLOSED = 'Closed',
}

export type CCProps = {
  distance: string
  openingTime: string
  closingTime: string
  countyName: string
  postCode: string
  name: string
  addLine2: string
  town: string
  latitude?: string
  longitude?: string
}

export type OccasionQuickLinkProps = {
  label: string
  roseId?: string
  isPostcodeChecker?: boolean
  isProductPageOccasion?: boolean
  occasionReminders?: OccasionReminder
}

export type RenderCommonOccasionProps = {
  shouldShowSelectDropdown: boolean
  displayAllOccasions: OccasionQuickLinkProps[]
  matchedOccasions: OccasionQuickLinkProps[]
  showAllOccasions?: boolean
  selectedOccasion: string
  handleChange: (event: SelectChangeEvent, isButtonClick?: boolean) => void
  setShowAllOccasions?: (val: boolean) => void
  classes: Record<string, string>
  occasionsList: OccasionQuickLinkProps[]
}

export type ClickAndCollectAnalytics = {
  sendClickAndCollectLoadMoreAnalytics?: () => void
  sendClickAndCollectCloseModalAnalytics?: () => void
  sendClickAndCollectSelectFloristAnalytics?: (floristSelected: string, positionInList: number) => void
  sendClickAndCollectStartNewSearchAnalytics?: () => void
}

export type CommonPDPProps = {
  sendPersonalMessageLocationToAnalytics?: (location: string) => void
  locationInternationalDelivery?: (click_location: string) => void
  ccProductAvailabilityCallBack?: CCProductAvailability
  sendDeliveryOptionAnalytics?: (serviceCode: string) => void
  sendClickAndCollectStatusAnalytics?: (status: string) => void
  isAddNewButtonClicked?: boolean
  setIsAddNewButtonClicked?: (isAddNewButtonClicked?: boolean) => void
  trackEnterManualAddressClick?: (click_location: string) => void
  sendPersonalisedMessageAnalytics?: (occasion: string) => void
  sendClickAndCollectMapOrListViewAnalytics?: () => void
  sendDeliveryTypesClickAnalytics?: (deliveryType: string) => void
  setShowInternationalLink?: (showInternationalLink: boolean) => void
  showInternationalLink?: boolean
}

export type BirthdayCampaignFormValues = {
  firstName: string
  lastName: string
  email: string
  dateOfBirth: string
  privacyPolicyCheck: boolean
}
